import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  DocumentArrowDownIcon,
  PrinterIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { rankingsApi } from "../../api/rankings";
import { cabinGroupsApi } from "../../api/cabinGroups";
import { cabinsApi } from "../../api/cabins";
import { Badge } from "../../components/ui/Badge";
import { isCamperCabin } from "../../utils/cabinFilter";
import { formatCabinNumber, normalizeDivisionName } from "../../utils/terminology";
import type {
  RankingMetric,
  RankingScope,
  RankingSortBy,
  RankingsResult,
  Division,
  Cabin,
  CenturyClubResponse,
} from "../../types";

const SCOPE_OPTIONS: { value: RankingScope; label: string }[] = [
  { value: "all-time", label: "All-Time" },
  { value: "current-term", label: "Current Term" },
  { value: "active", label: "Active Campers" },
];

const METRIC_OPTIONS: { value: RankingMetric; label: string }[] = [
  { value: "acs", label: "Most ACs" },
  { value: "honors", label: "Most Honors" },
  { value: "graduations", label: "Most Graduations" },
];

const TOP_N_OPTIONS = [10, 25, 50, 100, 0] as const;
function topNLabel(n: number): string {
  return n === 0 ? "All" : `Top ${n}`;
}

interface RankingsTabProps {
  termId: number | null;
  termLabel: string;
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-forest-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function RankingsTab({ termId, termLabel }: RankingsTabProps) {
  const [metric, setMetric] = useState<RankingMetric>("acs");
  const [scope, setScope] = useState<RankingScope>("all-time");
  const [sortBy, setSortBy] = useState<RankingSortBy>("count");
  const [topN, setTopN] = useState<number>(25);
  const [divisionId, setDivisionId] = useState<number | "">("");
  const [team, setTeam] = useState<"" | "army" | "navy">("");
  const [cabinId, setCabinId] = useState<number | "">("");
  const [asOfDate, setAsOfDate] = useState<string>("");

  const [data, setData] = useState<RankingsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [century, setCentury] = useState<CenturyClubResponse | null>(null);
  const [centuryOpen, setCenturyOpen] = useState(false);

  useEffect(() => {
    cabinGroupsApi.listDivisions().then(setDivisions).catch(() => {});
    cabinsApi.list().then(setCabins).catch(() => {});
    rankingsApi.centuryClub().then(setCentury).catch(() => {});
  }, []);

  const effectiveTermId = scope === "current-term" ? termId : null;
  const cantUseCurrentTerm = scope === "current-term" && termId == null;

  useEffect(() => {
    if (cantUseCurrentTerm) {
      setData({ rows: [], totalCampers: 0, metric, scope, asOfDate: null, termId: null });
      return;
    }
    setLoading(true);
    rankingsApi
      .list({
        metric,
        scope,
        sortBy,
        termId: effectiveTermId ?? undefined,
        divisionId: divisionId === "" ? undefined : divisionId,
        team: team === "" ? undefined : team,
        cabinId: cabinId === "" ? undefined : cabinId,
        asOfDate: asOfDate || undefined,
        limit: topN === 0 ? undefined : topN,
      })
      .then(setData)
      .catch(() => toast.error("Failed to load rankings"))
      .finally(() => setLoading(false));
  }, [metric, scope, sortBy, topN, divisionId, team, cabinId, asOfDate, effectiveTermId, cantUseCurrentTerm]);

  const sortedDivisions = useMemo(
    () => [...divisions].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
    [divisions]
  );
  const sortedCabins = useMemo(
    () => cabins
      .filter((c) => isCamperCabin({ name: c.name }))
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
    [cabins]
  );

  function downloadCsv() {
    const query = {
      metric,
      scope,
      sortBy,
      termId: effectiveTermId ?? undefined,
      divisionId: divisionId === "" ? undefined : divisionId,
      team: team === "" ? undefined : (team as "army" | "navy"),
      cabinId: cabinId === "" ? undefined : cabinId,
      asOfDate: asOfDate || undefined,
      limit: topN === 0 ? undefined : topN,
    };
    rankingsApi
      .exportCsv(query)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rankings-${metric}-${scope}${asOfDate ? `-${asOfDate}` : ""}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("CSV export failed"));
  }

  function openPrint() {
    const params = new URLSearchParams();
    params.set("metric", metric);
    params.set("scope", scope);
    params.set("sortBy", sortBy);
    if (effectiveTermId != null) params.set("termId", String(effectiveTermId));
    if (divisionId !== "") params.set("divisionId", String(divisionId));
    if (team !== "") params.set("team", team);
    if (cabinId !== "") params.set("cabinId", String(cabinId));
    if (asOfDate) params.set("asOfDate", asOfDate);
    if (topN !== 0) params.set("limit", String(topN));
    if (termLabel) params.set("termLabel", termLabel);
    window.open(`/reports/rankings/print?${params.toString()}`, "_blank", "noopener");
  }

  const metricLabel = METRIC_OPTIONS.find((m) => m.value === metric)!.label;
  const scopeLabel = SCOPE_OPTIONS.find((s) => s.value === scope)!.label;

  return (
    <div>
      {/* Sub-tabs for metric */}
      <div className="flex items-center gap-1 mb-4">
        {METRIC_OPTIONS.map((m) => (
          <button
            key={m.value}
            onClick={() => setMetric(m.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              metric === m.value
                ? "bg-forest-100 text-forest-800 border border-forest-200"
                : "text-stone-600 hover:bg-stone-100 border border-transparent"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Scope</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as RankingScope)}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            {SCOPE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Division</label>
          <select
            value={divisionId}
            onChange={(e) => setDivisionId(e.target.value === "" ? "" : Number(e.target.value))}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            <option value="">All divisions</option>
            {sortedDivisions.map((d) => (
              <option key={d.id} value={d.id}>{normalizeDivisionName(d.name)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Team</label>
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value as "" | "army" | "navy")}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            <option value="">All teams</option>
            <option value="army">Army</option>
            <option value="navy">Navy</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Cabin</label>
          <select
            value={cabinId}
            onChange={(e) => setCabinId(e.target.value === "" ? "" : Number(e.target.value))}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            <option value="">All cabins</option>
            {sortedCabins.map((c) => (
              <option key={c.id} value={c.id}>{formatCabinNumber(c.name)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Sort by</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as RankingSortBy)}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            <option value="count">Total count</option>
            <option value="per-year">Per year attended</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">Show</label>
          <select
            value={topN}
            onChange={(e) => setTopN(Number(e.target.value))}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          >
            {TOP_N_OPTIONS.map((n) => (
              <option key={n} value={n}>{topNLabel(n)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1">As of date</label>
          <input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="rounded-md border border-stone-300 text-sm px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-forest-500"
          />
        </div>
        <div className="ml-auto flex items-end gap-2">
          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium px-3 py-1.5 rounded-md border border-stone-300 bg-white hover:bg-stone-50"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={openPrint}
            className="inline-flex items-center gap-1.5 text-sm text-forest-700 hover:text-forest-900 font-medium px-3 py-1.5 rounded-md border border-stone-300 bg-white hover:bg-stone-50"
          >
            <PrinterIcon className="w-4 h-4" />
            Print View
          </button>
        </div>
      </div>

      {/* Century Club card */}
      {century && century.members.length > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-3">
          <TrophyIcon className="w-6 h-6 text-amber-700 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              Century Club &mdash; {century.members.length} {century.members.length === 1 ? "camper" : "campers"} with {century.threshold}+ lifetime ACs
            </div>
            <button
              onClick={() => setCenturyOpen((v) => !v)}
              className="text-xs text-amber-800 hover:text-amber-900 underline mt-0.5"
            >
              {centuryOpen ? "Hide list" : "Show list"}
            </button>
            {centuryOpen && (
              <div className="mt-2 text-sm text-amber-900 flex flex-wrap gap-x-3 gap-y-1">
                {century.members.map((m) => (
                  <Link
                    key={m.camperId}
                    to={`/campers/${m.camperId}`}
                    className="hover:underline whitespace-nowrap"
                  >
                    {m.firstName} {m.lastName}
                    <span className="text-amber-700 ml-1">({m.lifetimeAcs})</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Headline */}
      <div className="mb-3 text-sm text-stone-600">
        Showing <span className="font-semibold text-stone-800">{metricLabel}</span> ·{" "}
        <span className="font-semibold text-stone-800">{scopeLabel}</span>
        {scope === "current-term" && termLabel && <> · {termLabel}</>}
        {asOfDate && <> · as of {asOfDate}</>}
        {data && <> · {data.totalCampers} campers ranked</>}
      </div>

      {cantUseCurrentTerm ? (
        <p className="text-center text-stone-400 py-12 text-sm">
          Current-term rankings require a selected term.
        </p>
      ) : loading ? (
        <Spinner />
      ) : !data || data.rows.length === 0 ? (
        <p className="text-center text-stone-400 py-12 text-sm">No ranked campers match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="min-w-full divide-y divide-stone-200 text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide w-12">Rank</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Camper</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Division</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Team</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Cabin</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide whitespace-nowrap">{metricLabel.replace("Most ", "")}</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide whitespace-nowrap">Yrs</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wide whitespace-nowrap">Per Year</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-400 uppercase tracking-wide whitespace-nowrap">L-ACs</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-400 uppercase tracking-wide whitespace-nowrap">L-Honors</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-stone-400 uppercase tracking-wide whitespace-nowrap">L-Grads</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {data.rows.map((row) => (
                <tr key={row.camper.id} className="hover:bg-stone-50">
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-stone-700">{row.rank}</td>
                  <td className="px-4 py-3 font-medium text-stone-800">
                    <Link to={`/campers/${row.camper.id}`} className="hover:text-forest-700 hover:underline">
                      {row.camper.firstName} {row.camper.lastName}
                    </Link>
                    {row.camper.formerCamper && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-stone-100 text-stone-600">
                        Now Staff
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-stone-500">{row.divisionName ? normalizeDivisionName(row.divisionName) : "—"}</td>
                  <td className="px-4 py-3">
                    {row.camper.team === "army" && <Badge label="Army" color="army" />}
                    {row.camper.team === "navy" && <Badge label="Navy" color="navy" />}
                    {!row.camper.team && <span className="text-stone-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-stone-500">{row.cabinName ? formatCabinNumber(row.cabinName) : "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-forest-700">{row.count}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-stone-500">{row.yearsAttended}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-stone-500">{row.perYear.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-stone-400">{row.lifetimeAcs}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-stone-400">{row.lifetimeHonors}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-stone-400">{row.lifetimeGraduations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
